import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Checkbox } from '@material-ui/core';
import Modal from '../../../../../shared/components/core-ui/modal/modal.component';
import Button, {
  ButtonSize,
  ButtonType
} from '../../../../../shared/components/core-ui/button/button.component';
import {
  InputText,
  InputTextType
} from '../../../../../shared/components/core-ui/input-text/input-text.component';
import { getAllUnitTypes } from '../../../../../shared/services/data/unit-type.data';
import { bulkUpdateUnitTypeData } from '../../../../shared/services/data.service';
import { getModuleName } from '../../../../shared/services/unit-type.service';
import { birdToast } from '../../../../../shared/components/core-ui/toast';
import {
  UnitTypeUiModel,
  UnitTypeUiModelResponse
} from '../../../../../shared/ui-models/unit-type.ui-model';
import { isErrorInfo } from '../../../../../shared/interfaces/error-info.interface';
import useWindowResizer from '../../../../../shared/hooks/window-resizer/window-resizer.hook';

export type ApplyDataType = 'unitPriceInUSD' | 'customPrices' | 'ratePlans' | 'fees';

export interface ApplyToOtherUnitsModalProps {
  config: {
    showModal: boolean;
    currentUnitTypeId: string;
    currentPropertyId?: string;
  };
  data: {
    dataType: ApplyDataType;
    dataToApply: Partial<UnitTypeUiModel>;
    dataPreview: string;
  };
  eventHandlers: {
    closeHandler: () => void;
    onSuccess?: () => void;
  };
}

const DATA_TYPE_LABELS: Record<ApplyDataType, string> = {
  unitPriceInUSD: 'Unit Price',
  customPrices: 'Dynamic Prices',
  ratePlans: 'Rate Plans',
  fees: 'Fees'
};

const MAX_SELECTED_UNITS = 20;

export const ApplyToOtherUnitsModal: React.FC<ApplyToOtherUnitsModalProps> = ({
  config,
  data,
  eventHandlers
}) => {
  const isMobileView = useWindowResizer();

  const [unitTypes, setUnitTypes] = useState<UnitTypeUiModel[]>([]);
  const [selectedUnitIds, setSelectedUnitIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const dataTypeLabel = DATA_TYPE_LABELS[data.dataType];

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const unitsResponse = await getAllUnitTypes(
        getModuleName(),
        0,
        0,
        config.currentPropertyId ? { propertyId: config.currentPropertyId } : {}
      );

      if (!isErrorInfo(unitsResponse)) {
        setUnitTypes((unitsResponse as UnitTypeUiModelResponse).unitList || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [config.currentPropertyId]);

  useEffect(() => {
    if (!config.showModal) return;
    fetchData();
  }, [config.showModal, fetchData]);

  const filteredUnitTypes = useMemo(() => {
    let filtered = unitTypes.filter((unit) => unit.id !== config.currentUnitTypeId);

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (unit) =>
          unit.name?.toLowerCase().includes(query) ||
          unit.propertyName?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [unitTypes, searchQuery, config.currentUnitTypeId]);

  const toggleUnitSelection = (unitId: string) => {
    setSelectedUnitIds((prev) => {
      const next = new Set(prev);
      if (next.has(unitId)) {
        next.delete(unitId);
      } else {
        if (next.size >= MAX_SELECTED_UNITS) {
          return prev;
        }
        next.add(unitId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    const maxSelectableCount = Math.min(filteredUnitTypes.length, MAX_SELECTED_UNITS);

    if (selectedUnitIds.size === maxSelectableCount) {
      setSelectedUnitIds(new Set());
    } else {
      setSelectedUnitIds(
        new Set(
          filteredUnitTypes
            .slice(0, MAX_SELECTED_UNITS)
            .map((u) => u.id!)
            .filter(Boolean)
        )
      );
    }
  };

  const handleSubmit = () => {
    if (selectedUnitIds.size === 0) return;

    setIsSubmitting(true);

    const selectedUnits = unitTypes.filter((unit) => unit.id && selectedUnitIds.has(unit.id));
    const unitsToUpdate = selectedUnits.map((unit) => ({
      id: unit?.id,
      ...data.dataToApply
    }));

    const promise = bulkUpdateUnitTypeData(unitsToUpdate)
      .then((response) => {
        if (isErrorInfo(response)) {
          console.error('Error applying changes');
          throw response;
        }

        eventHandlers.onSuccess?.();
        setSelectedUnitIds(new Set());
        setSearchQuery('');
        eventHandlers.closeHandler();

        return response;
      })
      .finally(() => {
        setIsSubmitting(false);
      });

    birdToast.promise(promise, {
      title: 'Apply to Other Units',
      loadingMessage: 'Applying changes...',
      successMessage: `${DATA_TYPE_LABELS[data.dataType]} applied to ${
        selectedUnitIds.size
      } unit(s)`,
      errorFailedMessage: 'Failed to apply changes. Please try again.',
      errorPermissionMessage: 'Permission denied'
    });
  };

  return (
    <Modal
      config={{ showModal: config.showModal, fullScreen: false, showClose: true }}
      eventHandlers={{ closeModalHandler: eventHandlers.closeHandler }}
    >
      <div className="w-full p-4 md:p-8 font-gilroy-regular">
        <div className="mb-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
          <div className="text-[1.9rem] leading-tight font-gilroy-bold text-gray-900">
            Apply to Other Units
          </div>
          <div className="inline-flex items-center rounded-md border border-action-alert bg-action-alert bg-opacity-10 px-2 py-1 text-[1.2rem] text-neutral-800">
            <span className="mr-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-action-alert text-white text-[1rem] font-gilroy-bold">
              !
            </span>
            Replaces existing {dataTypeLabel.toLowerCase()}
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-gradient-to-br from-gray-50 to-white px-4 py-2 mb-3">
          <div className="flex items-baseline gap-2">
            <span className="text-[1.1rem] font-gilroy-medium uppercase tracking-[0.06em] text-gray-500">
              Data to apply:
            </span>
            <span className="text-[1.5rem] leading-snug font-gilroy-bold text-gray-900">
              {data.dataPreview}
            </span>
          </div>
        </div>

        <div className="mb-4">
          <div>
            <span className="block text-[1.2rem] font-gilroy-medium uppercase tracking-[0.07em] text-gray-500 mb-2">
              Search units
            </span>
            <InputText
              data={{
                placeholder: 'Search by unit name....',
                value: searchQuery
              }}
              config={{
                type: InputTextType.text
              }}
              eventHandlers={{
                onChangeHandler: (value: string) => setSearchQuery(value)
              }}
            />
            <div className="mt-1.5 text-[1.2rem] text-gray-500">
              You can apply to up to 20 units at once.
            </div>
          </div>
        </div>

        <div className="border border-gray-200 rounded-xl mb-6 overflow-hidden">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 px-4 py-3 border-b border-gray-200 bg-white">
            <div>
              <div className="text-[1.5rem] font-gilroy-bold text-gray-900">Units</div>
              <div className="text-[1.2rem] text-gray-500">
                {selectedUnitIds.size} selected out of{' '}
                {Math.min(filteredUnitTypes.length, MAX_SELECTED_UNITS)} available
              </div>
            </div>
            <Button
              onClick={toggleSelectAll}
              type={ButtonType.primaryInverted}
              size={ButtonSize.small}
              disabled={filteredUnitTypes.length === 0}
            >
              {selectedUnitIds.size === Math.min(filteredUnitTypes.length, MAX_SELECTED_UNITS) &&
              filteredUnitTypes.length > 0
                ? 'Clear All'
                : 'Select Top 20'}
            </Button>
          </div>

          {isLoading ? (
            <div className="p-8 text-center text-gray-500">Loading units...</div>
          ) : filteredUnitTypes.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No units found</div>
          ) : (
            <div className="max-h-[300px] overflow-y-auto">
              {filteredUnitTypes.map((unit) => {
                const isSelected = selectedUnitIds.has(unit.id!);
                const isSelectionLimitReached =
                  selectedUnitIds.size >= MAX_SELECTED_UNITS && !isSelected;

                return (
                  <button
                    key={unit.id}
                    type="button"
                    className={
                      `flex items-center w-full px-4 py-3 border-b border-gray-200 last:border-b-0 ` +
                      `bg-transparent text-left transition-colors duration-150 ` +
                      (isSelected
                        ? 'bg-gray-50 border-l-2 border-l-primary-500'
                        : isSelectionLimitReached
                        ? 'opacity-60 cursor-not-allowed'
                        : 'hover:bg-gray-50')
                    }
                    disabled={isSelectionLimitReached}
                    onClick={() => toggleUnitSelection(unit.id!)}
                  >
                    <Checkbox
                      color="primary"
                      checked={isSelected}
                      disabled={isSelectionLimitReached}
                      onChange={() => toggleUnitSelection(unit.id!)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="ml-2">
                      <div
                        className={
                          'text-xl leading-snug ' +
                          (isSelected
                            ? 'font-gilroy-bold text-gray-900'
                            : 'font-gilroy-medium text-gray-800')
                        }
                      >
                        {unit.name || 'Untitled Unit'}
                      </div>
                      <div className="text-xl font-gilroy-regular text-gray-500 mt-0.5">
                        {unit.propertyName || 'No Property'}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="pt-4 border-t border-gray-200">
          <div className="flex flex-col justify-end md:flex-row gap-3">
            <Button
              onClick={eventHandlers.closeHandler}
              type={ButtonType.primaryInverted}
              size={isMobileView ? ButtonSize.full : ButtonSize.medium}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              size={isMobileView ? ButtonSize.full : ButtonSize.medium}
              disabled={selectedUnitIds.size === 0 || isSubmitting}
            >
              {isSubmitting
                ? 'Applying changes...'
                : `Apply to ${selectedUnitIds.size} Unit${selectedUnitIds.size !== 1 ? 's' : ''}`}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ApplyToOtherUnitsModal;
